import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {RefObject, useMemo, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {FixedPlateModalStep} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {DEFAULT_MEASUREMENT} from 'constants/MeasurementConstants';
import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import AISuggestedLayoutBadge from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AISuggestedLayoutBadge';
import useAIRoleAnnotationSuggestion from 'samples/plate-maps/annotate-plate-modal/ai-assistant/useAIRoleAnnotationSuggestion';
import AnnotatePlateSettings from 'samples/plate-maps/annotate-plate-modal/AnnotatePlateSettings';
import {ProvidedConcentrationLayerSettings} from 'samples/plate-maps/annotate-plate-modal/ConcentrationLayerSettings';
import WellRoleAssignmentSettings from 'samples/plate-maps/annotate-plate-modal/WellRoleAssignmentSettings';
import {PlateMapDisplayAttributeState} from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import PlateMapPreviewSection from 'samples/plate-maps/plate-map-editor/PlateMapPreviewSection';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  CONCENTRATION_ATTRIBUTE_SENTINEL,
  ConcentrationAttribute,
  PlateMap,
  PlateMapWellDisplayLayer,
  PlateModalAnalyticsMetadata,
  SchemaFieldAssignmentGroup,
  SchemaFieldDisplayLayer,
  SchematizedAttribute,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import {
  clearPlateMapLayer,
  getDisplayLayerAttributeKey,
  getFlattenedWellIndex,
  isSchematizedAttribute,
} from 'samples/plate-maps/PlateMapUtil';
import PlateMapValidationBanners from 'samples/plate-maps/validation/PlateMapValidationBanners';
import {
  clearSchemaFieldErrors,
  getErrorKeyForDisplayLayerAttribute,
} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {
  FieldValuePlateMapError,
  LayerPlateMapError,
  PlateMapAnnotationError,
  PlateMapErrorType,
  ValidationErrorsByLayerAndLevel,
  WellPlateMapError,
} from 'samples/plate-maps/validation/ValidationTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT} from 'ui-library/Colors';

export interface AnnotatePlateStepProps {
  plateMap: PlateMap;
  setPlateMap: (plateMap: PlateMap) => void;
  initialSelectedWellIndices: Set<number>;
  maxPlateViewHeight: string;
  modalRef?: RefObject<HTMLDivElement>;
  analyticsMetadata: PlateModalAnalyticsMetadata;
  displayState: PlateMapDisplayAttributeState;
  showFinalMeasurements: boolean;
  isViewOnly?: boolean;
  groupedValidationErrors?: ValidationErrorsByLayerAndLevel;
  onRemoveLayer: (selectedAttribute: SchematizedAttribute | ConcentrationAttribute) => void;
  onDismissErrors: (errorUuids: Set<string>) => void;
  /** Type of the plate map; used to render slightly different errors/warnings for designs. */
  plateMapType: PlateMapType;
}

/**
 * Content of modal step that applies role and schema field value annotations to a plate.
 */
export default function AnnotatePlateStep({
  plateMap,
  setPlateMap,
  initialSelectedWellIndices,
  modalRef,
  analyticsMetadata,
  maxPlateViewHeight,
  displayState,
  showFinalMeasurements,
  isViewOnly,
  groupedValidationErrors,
  onRemoveLayer,
  onDismissErrors,
  plateMapType,
}: AnnotatePlateStepProps): JSX.Element {
  const previewSectionRef = useRef<HTMLDivElement>(null);
  const previewHeaderRef = useRef<HTMLDivElement>(null);
  const previewHeaderTextRef = useRef<HTMLParagraphElement>(null);

  const [selectedWellIndices, setSelectedWellIndices] = useState<Set<number>>(initialSelectedWellIndices);
  const [plateMapWithPreview, setPlateMapWithPreview] = useState<PlateMap | null>(null);
  const {assignmentGroupsPerAttribute, setAssignmentGroupsPerAttribute, selectedAttribute, displayLayer} =
    displayState;
  const [previewWellRoles, setPreviewWellRoles] = useState<boolean>(false);

  const {
    aiSuggestedPlateMap,
    clearAISuggestion,
    isAIPreview,
    handleSubmitAIPrompt,
    applyAISuggestion,
    discardAISuggestion,
  } = useAIRoleAnnotationSuggestion({
    plateMap,
    setPlateMapWithPreview,
    onApply: (suggestedPlateMap) => {
      trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.APPLIED_ROLE_ANNOTATION_AI_SUGGESTION);
      onEditPlateMap(suggestedPlateMap, {annotatedByAi: true});
    },
    onDiscard: () => {
      trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.DISCARDED_ROLE_ANNOTATION_AI_SUGGESTION);
    },
  });

  function deselectWells(): void {
    setSelectedWellIndices(new Set());
  }

  function trackEventWithStep(event: analytics.AnalyticsEvent): void {
    analytics.trackEvent(event, {
      ...analyticsMetadata,
      step: FixedPlateModalStep.ANNOTATE_PLATE_STEP,
    });
  }

  function handleClickClear(): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CLICKED_CLEAR_CELLS);
    const updatedPlateMap = clearPlateMapLayer(
      plateMap,
      displayLayer,
      selectedAttribute,
      selectedWellIndices
    );
    setPlateMap(updatedPlateMap);
    deselectWells();
  }

  function setAssignmentGroupsForSelectedAttribute(newGroupsById: {
    [id: string]: AssignmentGroup<AssignmentGroupValue>;
  }): void {
    setAssignmentGroupsPerAttribute({
      ...assignmentGroupsPerAttribute,
      [getDisplayLayerAttributeKey(selectedAttribute)]: newGroupsById,
    });
  }

  function onChangeWellSelection(indices: Set<number>): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CHANGED_CELL_SELECTION);
    setSelectedWellIndices(indices);
  }

  function onEditPlateMap(
    newPlateMap: PlateMap,
    additionalMetadata: {
      primaryRole?: PrimaryExperimentalRoleType;
      subrole?: string | null;
      annotatedByAi?: boolean;
      layerUsesDilution?: boolean;
    }
  ): void {
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.EDITED_ANNOTATIONS, {
      ...analyticsMetadata,
      ...additionalMetadata,
    });
    setPlateMap(newPlateMap);
  }

  function onResolveErrors(errors: Array<PlateMapAnnotationError>): void {
    const errorsByType = groupBy(errors, (e) => e.type);
    const hasLayerErrors = (errorsByType[PlateMapErrorType.LAYER] ?? []).length > 0;

    if (hasLayerErrors) {
      // Layer errors (e.g., duplicate concentration layer names) - remove the layer
      const layerError = errorsByType[PlateMapErrorType.LAYER][0] as LayerPlateMapError;
      assert(
        layerError.layerId === getErrorKeyForDisplayLayerAttribute(selectedAttribute),
        'Only expected to resolve errors for the current layer'
      );
      onRemoveLayer(selectedAttribute as SchematizedAttribute | ConcentrationAttribute);
    } else if (
      typeof selectedAttribute !== 'string' &&
      selectedAttribute.type === CONCENTRATION_ATTRIBUTE_SENTINEL
    ) {
      // Well errors on concentration layers (e.g., invalid measurements) - clear the values
      const wellErrors = (errorsByType[PlateMapErrorType.WELL] ?? []) as Array<WellPlateMapError>;
      const layerId = selectedAttribute.key;
      const wellErrorIndices = wellErrors.map(({coordinates}) =>
        getFlattenedWellIndex(coordinates.row, coordinates.column, plateMap.size)
      );
      const updatedPlateMap = produce(plateMap, (draft) => {
        for (const wellIndex of wellErrorIndices) {
          draft.wells[wellIndex].concentrationLayers[layerId] = DEFAULT_MEASUREMENT;
        }
      });
      setPlateMap(updatedPlateMap);
    } else {
      // Well/field errors on schema fields - clear invalid schema field values
      assert(
        isSchematizedAttribute(selectedAttribute),
        'Expected schematized attribute for schema field errors'
      );
      const updatedPlateMap = clearSchemaFieldErrors<SchemaFieldDisplayLayer>({
        plateMap,
        selectedAttribute,
        assignmentGroupsById: assignmentGroupsById as {[id: string]: SchemaFieldAssignmentGroup},
        wellErrors: (errorsByType[PlateMapErrorType.WELL] ?? []) as Array<WellPlateMapError>,
        fieldValueErrors: (errorsByType[PlateMapErrorType.FIELD_VALUE] ??
          []) as Array<FieldValuePlateMapError>,
      });
      setPlateMap(updatedPlateMap);
    }
  }

  const errorsForLayer =
    groupedValidationErrors != null
      ? (groupedValidationErrors[getErrorKeyForDisplayLayerAttribute(selectedAttribute)] ?? null)
      : null;

  const shouldDisableWellRolePreview = useMemo(
    () => plateMap.wells.every((well) => well.role == null),
    [plateMap]
  );

  const assignmentGroupsById = useMemo(
    () => assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {},
    [assignmentGroupsPerAttribute, selectedAttribute]
  );

  // Override display layer to show well roles when previewing
  const effectiveDisplayLayer = previewWellRoles ? PlateMapWellDisplayLayer.WELL_ROLE : displayLayer;
  const effectiveAttribute = previewWellRoles ? WELL_ROLE_ATTRIBUTE_SENTINEL : selectedAttribute;

  const plateMapDisplayParams = useMemo(
    () => ({
      plateMap: plateMapWithPreview ?? plateMap,
      displayLayer: effectiveDisplayLayer,
      attribute: effectiveAttribute,
      assignmentGroupsById,
      groupedValidationErrors: groupedValidationErrors ?? {},
    }),
    [
      assignmentGroupsById,
      effectiveDisplayLayer,
      effectiveAttribute,
      groupedValidationErrors,
      plateMap,
      plateMapWithPreview,
    ]
  );

  // If we are in AI preview mode, we want to show all wells as previewed. Otherwise, if there is a preview,
  // show only the selected wells as previewed.
  const previewedWellIndices = isAIPreview
    ? new Set(plateMap.wells.map((_, i) => i))
    : plateMapWithPreview != null
      ? selectedWellIndices
      : new Set<number>();

  const aiHeaderLeadingSlot =
    isAIPreview && displayLayer === PlateMapWellDisplayLayer.WELL_ROLE ? (
      <AISuggestedLayoutBadge />
    ) : undefined;

  return (
    <div className={css(styles.wrapper)} {...getTestProps({component: 'AnnotatePlateStep', element: 'root'})}>
      <PlateMapPreviewSection
        plateMapDisplayParams={plateMapDisplayParams}
        displayLayer={displayLayer}
        selectedWellIndices={selectedWellIndices}
        setSelectedWellIndices={onChangeWellSelection}
        previewedWellIndices={previewedWellIndices}
        totalWells={plateMap.wells.length}
        previewWellRoles={previewWellRoles}
        onTogglePreviewWellRoles={() => {
          setPreviewWellRoles(!previewWellRoles);
        }}
        shouldDisableWellRolePreview={shouldDisableWellRolePreview}
        onClickClear={handleClickClear}
        previewSectionRef={previewSectionRef}
        previewHeaderRef={previewHeaderRef}
        previewHeaderTextRef={previewHeaderTextRef}
        modalRef={modalRef}
        maxPlateViewHeight={maxPlateViewHeight}
        showFinalMeasurements={showFinalMeasurements}
        plateMapType={plateMapType}
        isViewOnly={isViewOnly}
        aboveHeader={
          errorsForLayer ? (
            <div className={css(styles.errorBanners)}>
              <PlateMapValidationBanners
                errorsByLevel={errorsForLayer}
                onResolveError={onResolveErrors}
                onDismissErrors={onDismissErrors}
              />
            </div>
          ) : null
        }
        headerLeadingSlot={aiHeaderLeadingSlot}
      />
      <div className={css(styles.border)} />
      <AnnotatePlateSettings
        plateMap={plateMap}
        onEditPlateMap={onEditPlateMap}
        selectedWellIndices={selectedWellIndices}
        deselectWells={deselectWells}
        displayLayer={displayLayer}
        selectedAttribute={selectedAttribute}
        assignmentGroupsById={assignmentGroupsById}
        setAssignmentGroupsById={setAssignmentGroupsForSelectedAttribute}
        wellRoleSettings={
          <WellRoleAssignmentSettings
            plateMap={plateMap}
            onEditPlateMap={onEditPlateMap}
            selectedWellIndices={selectedWellIndices}
            deselectWells={deselectWells}
            setPlateMapWithPreview={setPlateMapWithPreview}
            onSubmitAIPrompt={handleSubmitAIPrompt}
            onApplyAISuggestion={applyAISuggestion}
            onDiscardAISuggestion={discardAISuggestion}
            aiSuggestedPlateMap={aiSuggestedPlateMap}
            clearAISuggestion={clearAISuggestion}
          />
        }
        concentrationSettings={
          <ProvidedConcentrationLayerSettings
            plateMap={plateMap}
            onEditPlateMap={(updatedPlateMap, layerUsesDilution) => {
              onEditPlateMap(updatedPlateMap, {layerUsesDilution});
            }}
            selectedWellIndices={selectedWellIndices}
            deselectWells={deselectWells}
            previewPlateMap={plateMapWithPreview}
            setPlateMapWithPreview={setPlateMapWithPreview}
          />
        }
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    margin: '-16px',
    padding: '16px',
  },
  errorBanners: {
    marginBottom: '16px',
  },
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    margin: '-16px 16px',
  },
});
